import {
  CheckableSelectItem,
  CheckableTagSelectItem,
  DropdownSelect,
  Icon,
  IconNames,
  IconSizes,
  Tag,
} from '@dixa/lib-shared-habla';
import { PaginatedApiArg } from 'lib-shared-api-utils';
import { PaginatedApiResponse } from 'lib-shared-types-api';
import type { SearchQueueHit, SearchTagHit } from 'lib-shared-types-api';
import { SomePartial } from 'lib-shared-types-utils';
import unionBy from 'lodash/unionBy';
import xorBy from 'lodash/xorBy';
import {
  ComponentProps,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { searchApi } from 'apis/search';
import { languages } from 'app/utils/supportedLanguages';
import type { MatchingRuleT } from 'components/automations/components/nodeTypes/types';
import { VariableScope } from 'components/automations/components/nodeTypes/types';
import {
  useLazyPaginatedEndpoint,
  UseLazyPaginatedOptions,
  UsePaginatedResult,
} from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import type { MinimalReduxFormPropsT } from 'utils/minimalReduxFormFieldComponentAdapter';

import styles from './matchToken.module.scss';

type MatchTokenItemType = {
  id: number | string;
  name: string;
  color?: string;
  icon?: IconNames;
  iconSize?: IconSizes;
};

type MatchTokenMetaT = { label: string; color?: string };

type MatchTokenComponentProps = MinimalReduxFormPropsT<
  MatchingRuleT<MatchTokenMetaT>['match']
>;

type MatchTokenWrapperProps<TItemType extends MatchTokenItemType> = SomePartial<
  ComponentProps<typeof DropdownSelect<TItemType>>,
  'children'
>;

const MatchTokenWrapper = <TItemType extends MatchTokenItemType>(
  props: MatchTokenWrapperProps<TItemType>,
): JSX.Element => {
  return (
    <div className={styles.root}>
      <div className={styles.tokenContainer}>
        {(props.selectedItems as TItemType[])?.map((item) => {
          return (
            <Tag
              key={item.id}
              color={item.color}
              onDelete={() => props.onChange?.(item, false)}
              className={styles.token}
            >
              {item.name}
            </Tag>
          );
        })}
      </div>
      <DropdownSelect<TItemType>
        items={props.items}
        selectedItems={props.selectedItems}
        itemComponent={props.itemComponent}
        pagination={props.pagination}
        onToggle={props.onToggle}
        onChange={props.onChange}
        hasSearch={props.hasSearch}
        onSearch={props.onSearch}
        dropdownProps={{
          style: {
            width: '275px',
          },
        }}
        className={styles.addTokenContainer}
        buttonProps={{ className: styles.addTokenButton }}
        fixed
        keepOpen
      >
        <Icon name="dropdown-list" size={16} />
      </DropdownSelect>
    </div>
  );
};

const createStaticMatchTokenComponent = (items: MatchTokenItemType[]) => {
  function MatchToken({ input }: MatchTokenComponentProps) {
    const selectedItems = useMemo(
      (): MatchTokenItemType[] =>
        input.value?.values?.map((item) => {
          return {
            id: item.value,
            name: item.meta.label,
            color: item.meta.color,
          };
        }) || [],
      [input.value?.values],
    );

    const handleChange = useCallback(
      (item: MatchTokenItemType, checked: boolean) => {
        const newItems = (checked ? unionBy : xorBy)(
          selectedItems,
          [item],
          'id',
        );

        input.onChange({
          scope: VariableScope.INPUT,
          values: newItems.map((item) => {
            const meta: MatchTokenMetaT = { label: item.name };
            if (item.color) {
              meta.color = item.color;
            }

            return {
              value: item.id.toString(),
              meta,
            };
          }),
        });
      },
      [input, selectedItems],
    );

    return (
      <MatchTokenWrapper<MatchTokenItemType>
        items={items}
        selectedItems={selectedItems}
        itemComponent={CheckableSelectItem}
        onChange={handleChange}
      />
    );
  }

  return MatchToken;
};

const createDynamicMatchTokenComponent = <
  TPaginatedApiArg extends PaginatedApiArg,
  TPaginatedApiResponse extends PaginatedApiResponse,
  TSearchHit extends SearchQueueHit | SearchTagHit,
>(
  options: UseLazyPaginatedOptions<
    TPaginatedApiArg,
    TPaginatedApiResponse,
    TSearchHit
  >,
  defaultArg: Parameters<
    UsePaginatedResult<TSearchHit, TPaginatedApiArg>['changeArg']
  >[0],
  itemComponent = CheckableSelectItem,
): FunctionComponent<MatchTokenComponentProps> => {
  function MatchToken({ input }: MatchTokenComponentProps) {
    const [searchTerm, setSearchTerm] = useState<string>();

    const {
      items: hits,
      arg,
      changeArg,
      hasNextPage,
      nextPage,
    } = useLazyPaginatedEndpoint({ ...options, infinite: true });

    useEffect(() => {
      if (searchTerm !== arg?.searchTerm) {
        changeArg(
          {
            ...defaultArg,
            searchTerm,
            offset: undefined,
            limit: undefined,
          },
          {
            debounceTime: 300,
          },
        );
      }
      // Don't rely on arg as dependency because we change the arg inside.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changeArg, searchTerm]);

    const selectedItems = useMemo(
      (): MatchTokenItemType[] =>
        input.value?.values?.map((item) => {
          return {
            id: item.value,
            name: item.meta.label,
            color: item.meta.color,
          };
        }) || [],
      [input.value?.values],
    );

    const items = useMemo(
      (): MatchTokenItemType[] =>
        hits.map((hit) => ({
          id: hit.id,
          name: hit.name,
          color: (hit as SearchTagHit).color,
        })),
      [hits],
    );

    const handleSearch = useCallback((value?: string) => {
      setSearchTerm(value?.trim());
    }, []);

    const handleToggle = useCallback(
      (isOpen: boolean) => {
        if (isOpen && !hits.length) {
          changeArg(
            {
              ...defaultArg,
              searchTerm,
              offset: undefined,
              limit: undefined,
            },
            { debounceTime: 0 },
          );
        } else if (!isOpen && searchTerm !== '') {
          setSearchTerm('');
        }
      },
      [changeArg, hits.length, searchTerm],
    );

    const handleChange = useCallback(
      (item: MatchTokenItemType, checked: boolean) => {
        const newItems = (checked ? unionBy : xorBy)(
          selectedItems,
          [item],
          'id',
        );

        input.onChange({
          scope: VariableScope.INPUT,
          values: newItems.map((item) => {
            const meta: MatchTokenMetaT = { label: item.name };
            if (item.color) {
              meta.color = item.color;
            }

            return {
              value: item.id.toString(),
              meta,
            };
          }),
        });
      },
      [input, selectedItems],
    );

    return (
      <MatchTokenWrapper<MatchTokenItemType>
        items={items}
        selectedItems={selectedItems}
        itemComponent={itemComponent}
        pagination={{
          infinite: true,
          nextEnable: hasNextPage,
          previousEnable: false,
          onNext: () => Promise.resolve(nextPage()),
        }}
        onToggle={handleToggle}
        onChange={handleChange}
        hasSearch
        onSearch={handleSearch}
      />
    );
  }

  return MatchToken;
};

export const MatchTokenQueue = createDynamicMatchTokenComponent(
  {
    endpoint: searchApi.endpoints.readSearchQueue,
    itemsSelector: ({ data: { results } }) => results.hits ?? [],
  },
  {
    sort: { key: 'name', direction: 'asc' },
  },
);

export const MatchTokenTags = createDynamicMatchTokenComponent(
  {
    endpoint: searchApi.endpoints.readSearchTag,
    itemsSelector: ({ data: { results } }) => results.hits ?? [],
  },
  {
    sort: { key: 'name', direction: 'asc' },
  },
  CheckableTagSelectItem,
);

export const MatchTokenStatus = createStaticMatchTokenComponent([
  { id: 'closed', name: 'Closed' },
  { id: 'pending', name: 'Follow-up' },
  { id: 'open', name: 'Open' },
]);

export const MatchTokenLanguage = createStaticMatchTokenComponent(
  languages.map((lang) => ({ id: lang.code, name: lang.label })),
);
